import { useState, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

import {
  Alert,
  Button,
  DialogContent,
  DialogTitle,
  Snackbar,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material"

import HKVAnalytics from "./components/HKVAnalytics/1.0"
import HKVDialog from "./components/HKVDialog/7.0"
import HKVDrawer from "./components/HKVDrawer/14.0"
import HKVEChart from "./components/HKVEChart/2.0"
import HKVMarkdown from "./components/HKVMarkdown/6.0"

import Draggable from "react-draggable"

import i18n from "./i18n"

import moment from "moment"
import "moment/min/locales"

import HKVLoader from "./hkv_loader"

import "./App.css"

import { Rainsat } from "./pages"

import { clearErrors, clearRateLimit, removeNotification } from "./store/reducers/errors"
import { fetchLocalFile } from "./store/reducers/functions"
import { addLocalStorage } from "./store/reducers/localStorage"

import { getDisclaimer, getSupportEmail } from "./store/environment"

import { Navigate, Route, Routes } from "react-router-dom"
import { capitalizeFirstLetter, screenshotElement, screenshotViewport, summarizeMessage } from "./lib/utils"
import { decrement, increment } from "./store/reducers/loader"

import { checkSSOSession, getAccessToken, logout } from "./keycloak"

function App() {
  const dispatch = useDispatch()

  const accountinfo = useSelector((state) => state.accountinfo)
  const errors = useSelector((state) => state.errors)
  const rateLimit = useSelector((state) => state.rateLimit)
  const notifications = useSelector((state) => state.notifications)
  const loader = useSelector((state) => state.loader)
  const localStorage = useSelector((state) => state.localStorage)

  const { t } = useTranslation()
  const theme = useTheme()

  const webServerRef = useRef()

  const [initialized, setInitialized] = useState(false)

  const [dialogConfigs, setDialogConfigs] = useState({})

  const languages = [
    { short: "fr", long: "French" },
    { short: "en", long: "English" }
  ]

  const [selectedLanguage, setSelectedLanguage] = useState(localStorage.selectedLanguage
    ? localStorage.selectedLanguage
    : "en"
  )

  const [markdowns, setMarkdowns] = useState({})

  // const visibilityChanged = (state) => {
  //   if (state === "visible") {
  //     const queryParams = new URLSearchParams(window.location.search)
  //     if (queryParams.get("nowcast") && queryParams.get("country")) {
  //       let countryLabel = queryParams.get("country")
  //       countryLabel = countryLabel.charAt(0).toUpperCase() + countryLabel.slice(1)
  //       setSelectedCountry({ label: countryLabel, nowcast: JSON.parse(queryParams.get("nowcast")) })
  //       addRainsatLayer(JSON.parse(queryParams.get("nowcast")))
  //     }
  //   }
  //   setThreddsInterval(state === "visible") // Daarna elke x minuten
  // }

  // useInterval(() => {
  //   addRainsatLayer(selectedCountry ? selectedCountry.nowcast : false)
  // }, threddsInterval ? (1000 * 60 * 1) : null) // 2 minuten

  useEffect(() => {
    dispatch(checkSSOSession())
      .then(() => setInitialized(true))
  }, [])

  // claude: fetchMarkdownFiles hangt nu ook af van selectedLanguage - bij het wisselen van taal worden de
  // disclaimer/informatie/privacy-markdowns opnieuw opgehaald in de juiste taal (en/fr)
  useEffect(() => {
    i18n.changeLanguage(selectedLanguage)
    moment.locale(selectedLanguage)
    dispatch(addLocalStorage({ key: "selectedLanguage", value: selectedLanguage }))

    fetchMarkdownFiles(selectedLanguage, function (mds) {
      setMarkdowns(mds)
    })
  }, [selectedLanguage]) // eslint-disable-line

  // claude: HKVAnalytics (cookie-dialoog) verwijst naar de privacy-statement via dit event, zodat het de
  // interne Privacy-dialoog opent in plaats van door te linken naar de externe hkv.nl-pagina
  useEffect(() => {
    const handler = () => openDialog("privacy", "md")
    window.addEventListener("hkv-open-privacy-dialog", handler)
    return () => window.removeEventListener("hkv-open-privacy-dialog", handler)
  }, [])

  const fetchMarkdownFiles = async (language, callback) => {
    const lang = language === "fr" ? "fr" : "en"
    const disclaimerMD = await dispatch(fetchLocalFile(`/static/markdown/${lang}/disclaimer.md`))
    const informationMD = await dispatch(fetchLocalFile(`/static/markdown/${lang}/information.md`))
    const privacyMD = await dispatch(fetchLocalFile(`/static/markdown/${lang}/privacy.md`))

    if (disclaimerMD && informationMD && privacyMD) {
      callback({
        disclaimer: disclaimerMD,
        information: informationMD,
        privacy: privacyMD
      })
    }
  }

  const renderMarkdownDialog = (key, index) => {
    if (!dialogConfigs[key]) return

    const { title } = dialogConfigs[key]

    return (
      <HKVDialog
        key={index}
        config={dialogConfigs[key]}
        onClose={() => closeDialog(key)}
      >
        <DialogTitle sx={{ padding: theme.spacing(2, 3, 1) }}>{t(title || capitalizeFirstLetter(key))}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column" }}>
          <div style={{ overflow: "auto" }}>
            <HKVMarkdown
              source={markdowns[key]}
              className="markdownStyle"
            />
          </div>
          <div className="dialog-actions">
            <Button
              variant="contained"
              color="primary"
              className="dialog-button"
              onClick={() => closeDialog(key)}
            >
              {t("Close")}
            </Button>
          </div>
        </DialogContent>
      </HKVDialog>
    )
  }

  const updateEchartData = (instance) => {
    if (!instance) return

    const options = {
      textStyle: {
        fontFamily: "'Inter Variable', Inter, Roboto, 'Helvetica Neue', Arial, sans-serif",
        fontSize: 16
      },
      tooltip: {
        trigger: "axis"
      },
      grid: {
        top: 8,
        right: 8,
        bottom: 20,
        left: 34,
        containLabel: true
      },
      xAxis: {
        name: "Datetime",
        nameLocation: "center",
        type: "category",
        axisLabel: {
          hideOverlap: true,
          rotate: 67.5,
          formatter: function (value) {
            return moment(parseFloat(value)).format("DD-MM-YYYY HH:mm")
          }
        },
        axisPointer: {
          label: {
            formatter: function (params) {
              return moment(parseFloat(params.value)).format("DD-MM-YYYY HH:mm")
            }
          }
        }
      },
      yAxis: {
        name: "Precipitation [mm/h]",
        nameLocation: "center",
        type: "value"
      },
      dataset: [
        {
          id: "dataset_raw",
          source: dialogConfigs.chart.data.map((d => {
            return {
              datetime: moment(d.date).valueOf(),
              ...d
            }
          }))
        }
      ],
      series: [
        {
          name: "Precipitation",
          type: "bar",
          datasetId: "dataset_raw",
          showSymbol: false,
          encode: {
            x: "datetime",
            y: "value",
            itemName: "datetime",
            tooltip: [
              "value"
            ]
          }
        }
      ],
      color: [theme.palette.primary.main]
    }

    instance.setOption(options)
  }

  const chartDidMount = () => {
    setEChartInstances(prevState => {
      prevState[instance.key] = instance
      return { ...prevState }
    })

    updateEchartData(instance)
  }

  const renderChartDialog = () => {
    if (!dialogConfigs.chart) return null

    return (
      <HKVDialog config={dialogConfigs.chart} onClose={() => closeDialog("chart")}>
        <DialogTitle>{dialogConfigs.chart.address ? dialogConfigs.chart.address : "-"}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column" }}>
          <HKVEChart
            chartDidMount={chartDidMount}
            specName="line"
            height={450}
          />
          <div className="dialog-actions" style={{ marginTop: 0 }}>
            <Button
              color="primary"
              variant="contained"
              className="dialog-button"
              onClick={() => closeDialog("chart")}
            >
              {t("ok")}
            </Button>
          </div>
        </DialogContent>
      </HKVDialog>
    )
  }

  const openDialog = (key, size, options) => {
    const mergedOptions = { ...options, open: true, size: size ? size : "lg" }

    setDialogConfigs(prevState => {
      prevState[key] = mergedOptions
      return { ...prevState }
    })
  }

  const closeDialog = (key) => {
    setDialogConfigs(prevState => {
      prevState[key] = null
      return { ...prevState }
    })
  }

  const renderLoader = () => {
    if (loader > 0) {
      return (
        <div className="loader-background">
          <img rel="preload" as="image" src={HKVLoader} alt="HKV loader" className="loader-default" />
        </div>
      )
    } else return null
  }

  const renderWebServerMsg = () => {
    const disclaimer = getDisclaimer()
    if (!disclaimer) return null

    return (
      <Draggable bounds="parent" nodeRef={webServerRef}>
        <Snackbar
          ref={webServerRef}
          open={true}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          sx={{ zIndex: 99999999, cursor: "grab" }}
        >
          <Tooltip title={t("Drag_me")}>
            <Alert elevation={1} severity="info" sx={{ padding: theme.spacing(0, 1) }}>
              {t(disclaimer)}
            </Alert>
          </Tooltip>
        </Snackbar>
      </Draggable>
    )
  }

  // Everything support needs to reproduce the problem, collected in one mailto body.
  // Kept well under the ~2000 character limit that mail clients start truncating at.
  const reportErrors = () => {
    const address = getSupportEmail()
    if (!address) return

    const lines = [
      `${t("Reported")}: ${moment().format("DD-MM-YYYY HH:mm:ss")}`,
      `URL: ${window.location.href}`,
      `Browser: ${navigator.userAgent}`,
      "",
      `${t("Errors")}:`,
      ...errors.map((error, index) => [
        `${index + 1}. ${t("Message")}: ${String(error.message).replace(/\s+/g, " ").slice(0, 400)}`,
        `   ${t("Time")}: ${moment(error.key).format("DD-MM-YYYY HH:mm:ss")}`,
        `   ${t("Function")}: ${error.function}`,
        ...(error.number ? [`   ${t("Number")}: ${error.number}`] : []),
        ...(error.level ? [`   ${t("Level")}: ${error.level}`] : [])
      ].join("\n"))
    ]

    const body = lines.join("\n").slice(0, 1800)
    const subject = `Rainsat - ${t("Error_report")}`

    window.location.href = `mailto:${address}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  }

  // A rate limit is not a failure but a quota, so it gets its own dialog with a way out
  // (log in) instead of ending up between the technical errors
  const renderRateLimit = () => {
    if (!rateLimit) return null

    const resetAt = rateLimit.resetAt
      ? moment(rateLimit.resetAt)
      : rateLimit.retryAfter
        ? moment().add(rateLimit.retryAfter, "seconds")
        : null

    return (
      <HKVDialog
        config={{ open: true, size: "xs" }}
        onClose={() => dispatch(clearRateLimit())}
      >
        <DialogTitle>{t("Rate_limit_reached")}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column" }}>
          <Typography sx={{ marginBottom: theme.spacing(1) }}>
            {t("Rate_limit_explanation")}
          </Typography>
          <Typography sx={{ marginBottom: theme.spacing(1) }}>
            {accountinfo?.token ? t("Rate_limit_wait") : t("Rate_limit_login_hint")}
          </Typography>
          {resetAt && (
            <Typography variant="body2" color="text.secondary">
              {`${t("Try_again_after")}: ${resetAt.format("HH:mm:ss")}`}
            </Typography>
          )}
          <div className="dialog-actions">
            <Button
              color="inherit"
              variant="outlined"
              className="dialog-button"
              onClick={() => dispatch(clearRateLimit())}
            >
              {t("Close")}
            </Button>
            {!accountinfo?.token && (
              <Button
                variant="contained"
                color="primary"
                className="dialog-button"
                onClick={() => {
                  dispatch(clearRateLimit())
                  dispatch(getAccessToken())
                }}
              >
                {t("Login")}
              </Button>
            )}
          </div>
        </DialogContent>
      </HKVDialog>
    )
  }

  const renderErrors = () => {
    return (
      <HKVDialog
        config={{ open: errors.length > 0, size: "md" }}
        onClose={() => dispatch(clearErrors())}
      >
        <DialogTitle>{errors.length > 1 ? t("Multiple_errors_have_occurred") : t("An_error_has_occurred")}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", maxWidh: "100%", maxHeight: 400 }}>
          <div style={{ display: "flex", flexDirection: "column", overflow: "auto" }}>
            {errors.map((error, index) => (
              <div key={index} style={{ border: `1px solid ${theme.palette.grey[300]}`, borderRadius: theme.shape.borderRadius, padding: theme.spacing(0.5, 1), marginTop: index === 0 ? 0 : theme.spacing(0.5) }}>
                {/* claude: alleen de melding voor de gebruiker - tijd en functie zijn
                diagnostische velden en gaan mee in "Report errors" */}
                <Typography sx={{ overflowWrap: "anywhere" }}>{summarizeMessage(t(error.message))}</Typography>
              </div>
            ))}
          </div>
          <div className="dialog-actions">
            {Boolean(getSupportEmail()) && (
              <Button
                color="inherit"
                variant="outlined"
                className="dialog-button"
                onClick={() => reportErrors()}
              >
                {t("Report_errors")}
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              className="dialog-button"
              onClick={() => dispatch(clearErrors())}
            >
              {t("Close")}
            </Button>
          </div>
        </DialogContent>
      </HKVDialog>
    )
  }

  const closeNotification = (reason, index) => {
    if (reason === "clickaway") return null
    dispatch(removeNotification({ index }))
  }

  const renderNotifications = () => {
    const height = 50
    return notifications.map((notification, index) => {
      const bottom = (height * index)

      return (
        <Snackbar
          disableWindowBlurListener
          key={`${notification.key}-${index}`}
          open={true}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          autoHideDuration={notification.duration ? notification.duration : 4000}
          onClose={(event, reason) => closeNotification(reason, index)}
          onClick={() => closeNotification("click", index)}
          sx={{
            zIndex: 99999999,
            width: "fit-content",
            maxWidth: `calc(100% - ${theme.spacing(2)})`,
            height: height,
            left: 8,
            right: 8,
            margin: "0 auto",
            bottom: `calc(${bottom}px + ${theme.spacing(1 * (index + 1))}) !important`
          }}
        >
          <Alert elevation={2} severity={notification.type}>
            <Typography className="ellipsis">
              {notification.message.toString()}
            </Typography>
          </Alert>
        </Snackbar>
      )
    })
  }

  const renderSignoutDialog = () => {
    if (!dialogConfigs.signout) return null

    return (
      <HKVDialog config={dialogConfigs.signout} onClose={() => closeDialog("signout")}>
        <DialogTitle>{t("Logout_title")}</DialogTitle>
        <DialogContent sx={{ flexDirection: "column" }}>
          <div className="dialog-actions" style={{ margin: 0 }}>
            <Button
              color="inherit"
              variant="outlined"
              className="dialog-button"
              onClick={() => closeDialog("signout")}
            >
              {t("No")}
            </Button>
            <Button
              color="primary"
              variant="contained"
              className="dialog-button"
              onClick={() => {
                closeDialog("signout")
                logout()
              }}
            >
              {t("Yes")}
            </Button>
          </div>
        </DialogContent>
      </HKVDialog>
    )
  }

  const drawerItems = [
    {
      icon: "LinkedCameraRounded",
      label: "Capture_screenshot",
      placement: "bottom",
      function: async () => {
        dispatch(increment())
        await screenshotElement({ selector: "#screenshot", filename: `Rainsat-screenshot-${moment().format("YYYY-MM-DD HH:mm")}`, type: "jpg", quality: 0.75 })
        dispatch(decrement())
      }
    },
    {
      icon: "HelpRounded",
      label: "Information",
      placement: "bottom",
      selected: dialogConfigs["information"],
      function: () => openDialog("information", "md", { title: "Information" })
    },
    {
      icon: "InfoRounded",
      label: "Disclaimer",
      placement: "bottom",
      selected: dialogConfigs["disclaimer"],
      function: () => openDialog("disclaimer", "md", { title: "Disclaimer" })
    },
    {
      icon: "LockRounded",
      label: "Privacy_statement",
      placement: "bottom",
      selected: dialogConfigs["privacy"],
      function: () => openDialog("privacy", "md", { title: "Privacy_statement" })
    }
  ]

  if (!initialized) {
    return (
      <div className="App" id="app">
        {renderLoader()}
        {renderErrors()}
        {renderRateLimit()}
        {renderNotifications()}
        {renderWebServerMsg()}
        <HKVAnalytics />
      </div>
    )
  }

  return (
    <div className="App" id="app">
      {renderLoader()}
      <div id="screenshot" style={{ width: "100%", height: "100%" }}>
        {renderErrors()}
        {renderRateLimit()}
        {renderNotifications()}
        {renderWebServerMsg()}
        <HKVDrawer
          placement="left"
          header={{
            image: "/static/images/logo-alt-40.png",
            button: {
              visible: false,
              position: "absolute"
            }
          }}
          items={drawerItems}
          languages={languages}
          selectedLanguage={selectedLanguage}
          setSelectedLanguage={setSelectedLanguage}
          persistent={true}
          logout={true}

          doSignIn={() => dispatch(getAccessToken())}
          // doSignIn={() => alert(t("Not_implemented_yet"))}
          doSignOut={() => openDialog("signout", "xs")}
        />
        <div
          id="main"
          style={{
            display: "flex",
            margin: "0 0 0 56px",
            width: "calc(100% - 56px)",
            height: "100%",
            position: "relative",
            zIndex: 0
          }}
        >
          <Routes>
            <Route
              path="/"
              element={<Rainsat selectedLanguage={selectedLanguage} />}
            />
            <Route
              path="/projects/:project"
              element={<Rainsat selectedLanguage={selectedLanguage} />}
            />
            <Route path="*" element={<Navigate replace to="/" />} />
          </Routes>
        </div>
        {Object.keys(markdowns).map((mdKey, i) => {
          return renderMarkdownDialog(mdKey, i)
        })}
        {renderChartDialog()}
        {renderSignoutDialog()}
        {/* claude: laatst in de DOM zodat de cookie-dialoog (gelijke z-index, disablePortal) altijd boven
        de andere dialogen (o.a. Privacy) getekend wordt wanneer hij vanuit "cookie-instellingen" opnieuw
        geopend wordt */}
        <HKVAnalytics />
      </div>
    </div>
  )
}

export default App